import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Briefcase, Database, Loader2, Map, Settings, Users, Wrench, X, RefreshCw, Check
} from 'lucide-react';
import ErrorAlert from '../components/ErrorAlert';
import { smartCase } from '../utils/text';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70';
const SOFT_SHADOW =
  'shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)]';

// La API puede devolver la lista directamente o envuelta en { data: [...] }
const asList = (body) => (Array.isArray(body) ? body : body?.data || []);

const getAppIcon = (appName) => {
  const name = (appName || '').toLowerCase();
  if (name.includes('administracion') || name.includes('admin')) return Database;
  if (name.includes('mantenimiento')) return Wrench;
  if (name.includes('proyectos')) return Briefcase;
  if (name.includes('representantes') || name.includes('gremiales')) return Users;
  if (name.includes('sigev') || name.includes('vial')) return Map;
  return Settings;
};

const getUnidad = (app) => app.UnidadAdministrativa?.Descripcion || app.UnidadAdministrativa || '';

// Fila de dato: etiqueta pequeña arriba y valor debajo
const Field = ({ label, value, breakAll = false }) => (
  <div className="py-2.5 first:pt-0 last:pb-0">
    <dt className="text-xs text-neutral-400">{label}</dt>
    <dd className={`mt-0.5 text-sm font-medium text-cafe-800 ${breakAll ? 'break-all' : ''}`}>
      {value || <span className="font-normal text-neutral-300">No especificado</span>}
    </dd>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h3 className="mb-2.5 text-xs font-medium text-neutral-400">{title}</h3>
    <dl className={`divide-y divide-cafe-100/70 rounded-xl bg-white p-4 ${SOFT_SHADOW}`}>{children}</dl>
  </section>
);

const ProfileModal = ({ show, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('perfil');
  const [employeeData, setEmployeeData] = useState(null);
  const [userApplications, setUserApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const closeButtonRef = useRef(null);
  const scrollRef = useRef(null);

  // PortalMain se vuelve a renderizar cada segundo (reloj) y pasa un onClose nuevo cada vez:
  // se guarda en una referencia para que el efecto no se reinicie ni le quite el foco al usuario.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const nombreUsuario = user?.usuario || user?.Usuario || '';

  const fetchList = async (path) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) throw new Error(`Error al cargar datos de ${path}: ${response.status}`);
    return asList(await response.json());
  };

  const loadEmployeeData = async (idEmpleado) => {
    try {
      const empleados = await fetchList('/empleado');
      return empleados.find((emp) => emp.CodEmpleado === idEmpleado) || null;
    } catch (err) {
      console.warn('Error al cargar datos del empleado:', err);
      return null;
    }
  };

  const loadUserData = useCallback(async () => {
    if (!nombreUsuario) {
      setError('No se encontró información del usuario.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // El backend de Azure no tiene la ruta /api; la lista de usuarios está en /usuarios
      const usuarios = await fetchList('/usuarios');
      const usuarioActual = usuarios.find((u) => u.Usuario === nombreUsuario);
      if (!usuarioActual) throw new Error(`Usuario ${nombreUsuario} no encontrado en /usuarios`);

      setUserApplications(user.permisos?.Aplicaciones || usuarioActual.Roles || []);

      const empleadoCompleto = usuarioActual.IdEmpleadoFK
        ? await loadEmployeeData(usuarioActual.IdEmpleadoFK)
        : null;

      setEmployeeData(
        empleadoCompleto || {
          Nombres: usuarioActual.Empleado?.split(' ')[0] || nombreUsuario,
          Apellidos: usuarioActual.Empleado?.split(' ').slice(1).join(' ') || '',
          Direccion: usuarioActual.Direccion,
          Puesto: { Descripcion: usuarioActual.Puesto },
          Estado: 'ACTIVO'
        }
      );
    } catch (err) {
      console.error('Error al cargar datos del usuario:', err);
      setError('No se pudo cargar toda su información. Se muestran los datos básicos de su sesión.');
      setEmployeeData({ Nombres: nombreUsuario, Apellidos: '', Estado: 'ACTIVO' });
      setUserApplications(user.permisos?.Aplicaciones || []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nombreUsuario, user]);

  useEffect(() => {
    if (!show) return;

    setActiveTab('perfil');
    closeButtonRef.current?.focus();
    loadUserData();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [show, loadUserData]);

  if (!show) return null;

  const fullName = smartCase(
    [employeeData?.Nombres, employeeData?.Apellidos].filter(Boolean).join(' ') ||
      user?.empleado?.NombreEmpleado ||
      nombreUsuario ||
      'Usuario'
  );
  const initials = fullName
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const estado = smartCase(employeeData?.Estado || 'Activo');

  const tabs = [
    { id: 'perfil', label: 'Perfil' },
    { id: 'aplicaciones', label: 'Aplicaciones', count: userApplications.length }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 animate-fade-in bg-cafe-800/30 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-title"
        aria-busy={loading}
        className="relative flex max-h-[90vh] w-full max-w-lg animate-card-in flex-col overflow-hidden rounded-2xl bg-[#FBF8F4] shadow-[0_30px_80px_-30px_rgba(59,39,25,0.55)]"
      >
        <div className="absolute right-3 top-3 flex items-center gap-1">
          <button
            onClick={loadUserData}
            disabled={loading}
            title="Actualizar datos"
            aria-label="Actualizar datos"
            className={`rounded-lg p-2 text-neutral-400 transition-colors duration-200 hover:bg-cafe-100/50 hover:text-cafe-700 disabled:cursor-wait disabled:opacity-50 ${FOCUS}`}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            title="Cerrar"
            aria-label="Cerrar perfil"
            className={`rounded-lg p-2 text-neutral-400 transition-colors duration-200 hover:bg-cafe-100/50 hover:text-cafe-700 ${FOCUS}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Encabezado: iniciales, nombre y estado */}
        <header className="px-6 pb-5 pt-8 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-cafe-200/60 bg-white font-display text-xl font-semibold text-cafe-700">
            {initials}
          </span>
          <h2
            id="profile-title"
            className="mt-4 text-balance font-display text-2xl font-semibold leading-tight tracking-tight text-cafe-700"
          >
            {fullName}
          </h2>
          {nombreUsuario && <p className="mt-1 text-sm text-neutral-400">@{nombreUsuario}</p>}

          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-cafe-700 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-green-600" aria-hidden="true" />
              {estado}
            </span>
            {user?.esAdmin && (
              <span className="inline-flex items-center rounded-full bg-[#F6E7C3] px-2.5 py-1 text-xs font-medium text-cafe-700">
                Administrador
              </span>
            )}
          </div>
        </header>

        {/* Selector de sección */}
        <div className="flex justify-center px-6 pb-4">
          <div className="inline-flex rounded-lg bg-cafe-100/40 p-1" role="tablist" aria-label="Secciones del perfil">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (scrollRef.current) scrollRef.current.scrollTop = 0;
                }}
                className={`rounded-md px-4 py-1.5 text-sm transition-colors duration-200 ${FOCUS} ${
                  activeTab === tab.id
                    ? 'bg-white font-medium text-cafe-800 shadow-sm'
                    : 'text-neutral-400 hover:text-cafe-700'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && <span className="ml-1.5 text-xs text-neutral-300">{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 pb-6 [scrollbar-color:#C8A88C_transparent] [scrollbar-width:thin]"
        >
          {/* La key reinicia la animación de entrada al cambiar de pestaña */}
          <div key={activeTab} className="portal-view">
            {error && !loading && (
              <div className="mb-5">
                <ErrorAlert kind="server" message={error} />
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-cafe-500" />
                <p className="mt-3 text-sm text-neutral-400">Cargando información…</p>
              </div>
            ) : activeTab === 'perfil' ? (
              <div className="space-y-5">
                <Section title="Información laboral">
                  <Field label="Puesto" value={smartCase(employeeData?.Puesto?.Descripcion || '')} />
                </Section>

                <Section title="Contacto">
                  <Field label="Correo" value={employeeData?.Correo} breakAll />
                  <Field label="Teléfono" value={employeeData?.Telefono} />
                  <Field label="Dirección" value={smartCase(employeeData?.Direccion || '', { sentence: true })} />
                </Section>
              </div>
            ) : userApplications.length > 0 ? (
              <ul className="space-y-2.5">
                {userApplications.map((app, index) => {
                  const Icon = getAppIcon(app.NombreAplicacion);
                  const unidad = getUnidad(app);
                  return (
                    <li
                      key={app.Id ?? index}
                      className={`flex items-center gap-3 rounded-xl bg-white p-3.5 ${SOFT_SHADOW}`}
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-cafe-100/60 text-cafe-700">
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate font-display text-[15px] font-semibold text-cafe-800">
                          {smartCase(app.NombreAplicacion || '')}
                        </h4>
                        {unidad && <p className="truncate text-xs text-neutral-400">{smartCase(unidad)}</p>}
                      </div>
                      <Check className="h-4 w-4 flex-shrink-0 text-green-700" aria-label="Acceso autorizado" />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="py-14 text-center">
                <div
                  className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-cafe-500 ${SOFT_SHADOW}`}
                >
                  <Settings className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-semibold text-cafe-700">Sin aplicaciones asignadas</h3>
                <p className="mt-1 text-sm text-neutral-400">
                  Contacte al administrador para obtener acceso.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
